import RequestToken from '../../../../tokens/RequestToken';
import {CommandRequester} from '../../../../CommandRequester';
import RequestAPI from '../../../RequestAPI';
import {CommandTypes, FetchAssetEvents, UnloadAssetEvents} from '@jibo/command-protocol';

/**
 * @class FetchAssetToken
 * @extends RequestToken
 * @hideconstructor
 */
export class FetchAssetToken extends RequestToken<JIBO.v1.FetchAssetRequest, JIBO.v1.FetchAssetEvent> {

    /** @private */
    constructor(owner:CommandRequester, protocol: JIBO.v1.FetchAssetRequest) {
        super(owner, protocol);
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        const message = event.EventBody;
        switch (message.Event) {
            case FetchAssetEvents.AssetReady:
                this.isComplete = true;
                this._complete.resolve(message);
                break;
            case FetchAssetEvents.AssetFailed:
                this.isComplete = true;
                this._complete.reject(message);
                break;
        }
    }
}

/**
 * @class UnloadAssetToken
 * @extends RequestToken
 * @hideconstructor
 */
export class UnloadAssetToken extends RequestToken<JIBO.v1.UnloadAssetRequest, JIBO.v1.UnloadAssetEvent> {

    /** @private */
    constructor(owner:CommandRequester, protocol: JIBO.v1.UnloadAssetRequest) {
        super(owner, protocol);
    }

    /**
     * @private
     */
    public handleAck(ack:JIBO.v1.Acknowledgement) {
        //handle ack
        if (ack.Response.ResponseCode >= 400) {
            this.isComplete = true;
            this._complete.reject(ack.Response);
            return;
        }
    }

    /**
     * @private
     */
    public handleEvent(event:JIBO.v1.EventMessage) {
        //handle event
        const message = event.EventBody;
        switch (message.Event) {
            case UnloadAssetEvents.UnloadAssetDone:
                this.isComplete = true;
                this._complete.resolve(message);
                break;
            case UnloadAssetEvents.UnloadAssetFailed:
                this.isComplete = true;
                this._complete.reject(message);
                break;
        }
    }
}

/**
 * Internal only class for Assets
 * @class CommandRequester.assets.Assets
 * @intdocs
 */
export class Assets extends RequestAPI<JIBO.v1.FetchAssetRequest | JIBO.v1.UnloadAssetRequest, FetchAssetToken | UnloadAssetToken> {

    /**
     * Generate loadAsset Protocol
     * @method CommandRequester.assets.Assets#generateLoadProtocol
     * @param {string} uri - URI of the asset to be fetched.
     * @param {string} name - Name given to the asset for later reference (must be unique).
     * @returns {FetchAssetRequest}
     * @intdocs
     */
    static generateLoadProtocol(uri: string, name: string): JIBO.v1.FetchAssetRequest {
        return {
            Type: CommandTypes.FetchAsset,
            URI: uri,
            Name: name
        };
    }

    /**
     * Generate unloadAsset Protocol
     * @method CommandRequester.assets.Assets#generateUnloadProtocol
     * @param {string} name - Name given to the asset for later reference (must be unique).
     * @returns {UnloadAssetRequest}
     * @intdocs
     */
    static generateUnloadProtocol(name: string): JIBO.v1.UnloadAssetRequest {
        return {
            Type: CommandTypes.UnloadAsset,
            Name: name
        };
    }

    /**
     * @method CommandRequester.assets.Assets#generateToken
     * @description Create FetchAssetToken from FetchAssetRequest protocol.
     * @param {FetchAssetRequest | UnloadAssetRequest} protocol - FetchAssetRequest protocol to generate a FetchAssetToken from.
     * @param {boolean} [andSend=false] - `true` if the generated FetchAssetToken should also be sent as a request.
     * @return {FetchAssetToken}
     * @intdocs
     */
    public generateToken(protocol: JIBO.v1.FetchAssetRequest | JIBO.v1.UnloadAssetRequest, andSend=false): FetchAssetToken | UnloadAssetToken {
        let token: FetchAssetToken | UnloadAssetToken;
        if (protocol.Type === CommandTypes.FetchAsset) {
            token = new FetchAssetToken(this.owner, protocol);
        } else if (protocol.Type === CommandTypes.UnloadAsset) {
            token = new UnloadAssetToken(this.owner, protocol);
        }
        if (andSend) {
            super.sendToken(token);
        }
        return token;
    }

    /**
     * Send FetchAssetToken request.
     * <p>NOTE: Implementation in base class {@link RequestAPI}</p>
     * @method CommandRequester.assets.Assets#sendToken
     * @param {FetchAssetToken} token - FetchAssetToken to send.
     * @intdocs
     */
}
